import React, { FC } from 'react';
import { Box, Text } from '@bechtel/gds';
import { CalulationParametersStyled, TargetCalculationStyled } from './styled';
import { UnitOfMeasure, TargetHoursCalculation } from '@bechtel/types';
import { useConfig, ErrorBoundary, bankersRound } from '@bechtel/client-common';
import { PlanningTargetCalcProgressProps } from '../types';
import { Trans } from '@lingui/macro';

export const TargetHourPopperContent: FC<PlanningTargetCalcProgressProps> = ({
  indirectTax,
  projectAdjustmentFactor,
  compositeUnitRate,
}) => {
  const crewHeadCount = 100;
  const weeklyHours = 50;
  const crewHours = crewHeadCount * weeklyHours;
  const { showInLinearFeet } = useConfig();
  const targetHoursCalculation: TargetHoursCalculation = {
    crewHeadCount: crewHeadCount,
    weeklyHours: weeklyHours,
    indirectTax: indirectTax,
    projectAdjustmentFactor: projectAdjustmentFactor,
    //@ts-ignore avoid unnecesary assignment of mandatory props to generate declaration file
    compositeUnitRate: { unitRate: compositeUnitRate },
    crewHours: crewHours,
    indirectHours: (indirectTax * crewHours) / 100,
    projectAdjustmentHours: (crewHours * projectAdjustmentFactor) / 100,
    crewQuantity: {
      quantity: 10,
      unitOfMeasure: UnitOfMeasure.LINEAR_FEET,
    },
    indirectQuantity: {
      quantity: 5,
      unitOfMeasure: UnitOfMeasure.LINEAR_FEET,
    },
    projectAdjustmentQuantity: {
      quantity: 10,
      unitOfMeasure: UnitOfMeasure.LINEAR_FEET,
    },
  };
  return (
    <Box style={{ userSelect: 'none' }} flexDirection={{ sm: 'column' }}>
      <Box p={{ sm: '400' }}>
        <Text size="300" family="graphikCompact" color="grey.1000" weight="600">
          <Trans>Your Target Hour Calculation</Trans>
        </Text>
      </Box>
      <ErrorBoundary>
        <TargetCalculationStyled>
          <Box
            pr={{ sm: '400' }}
            flexDirection={{ sm: 'column' }}
            css={`
              ${({ theme }) => `border-right: 1px solid ${theme.colors.grey[200]};`}
            `}
          >
            <Box alignItems={{ sm: 'center' }}>
              <Trans>Headcount</Trans>
              <CalulationParametersStyled>{crewHeadCount}</CalulationParametersStyled>x
              <CalulationParametersStyled>{weeklyHours}</CalulationParametersStyled>
              <Trans>Hrs/Wk</Trans>
            </Box>
            <Box pt={{ sm: '400' }} alignItems={{ sm: 'center' }}>
              + <Trans>Indirect Tax</Trans>
              <CalulationParametersStyled data-testid="popper-indirect-tax">{`${targetHoursCalculation.indirectTax}%`}</CalulationParametersStyled>
            </Box>
            <Box pt={{ sm: '400' }} alignItems={{ sm: 'center' }}>
              <Trans>Project Multiplier</Trans>
              <CalulationParametersStyled data-testid="popper-project-multiplier">
                {targetHoursCalculation.projectAdjustmentFactor}%
              </CalulationParametersStyled>
            </Box>
            <Box pt={{ sm: '400' }}>
              <Box py={{ sm: '100' }}>
                <Text weight="600">
                  <Trans>Hours Target</Trans>
                </Text>
              </Box>
            </Box>
            {showInLinearFeet && (
              <Box pt={{ sm: '400' }}>
                <Box py={{ sm: '100' }}>
                  <Text weight="600">
                    <Trans>LF Target&nbsp;</Trans>
                  </Text>
                  <Text color="grey.600">
                    <em>
                      (<Trans>approx unit rate</Trans> {targetHoursCalculation.compositeUnitRate} <Trans>hr/ft</Trans>)
                    </em>
                  </Text>
                </Box>
              </Box>
            )}
          </Box>
          <Box pl={{ sm: '400' }} flexDirection={{ sm: 'column' }}>
            <Box alignItems={{ sm: 'center' }}>
              <Box py={{ sm: '100' }}>
                <Text>
                  {targetHoursCalculation.crewHours} <Trans>hrs</Trans>
                </Text>
              </Box>
            </Box>
            <Box pt={{ sm: '400' }} alignItems={{ sm: 'center' }}>
              <Box py={{ sm: '100' }}>
                <Text>
                  {targetHoursCalculation.indirectHours} <Trans>hrs</Trans>
                </Text>
              </Box>
            </Box>
            <Box pt={{ sm: '400' }} alignItems={{ sm: 'center' }}>
              <Box py={{ sm: '100' }}>
                <Text>
                  +{targetHoursCalculation.projectAdjustmentHours} <Trans>hrs</Trans>
                </Text>
              </Box>
            </Box>
            <Box pt={{ sm: '400' }} alignItems={{ sm: 'center' }}>
              <Box py={{ sm: '200' }}>
                <Text weight="600">
                  {targetHoursCalculation.crewHours +
                    targetHoursCalculation.indirectHours +
                    targetHoursCalculation.projectAdjustmentHours}
                  &nbsp;<Trans>hrs</Trans>
                </Text>
              </Box>
            </Box>
            {showInLinearFeet && (
              <Box pt={{ sm: '400' }} alignItems={{ sm: 'center' }}>
                <Box py={{ sm: '100' }}>
                  <Text weight="600">
                    {`${
                      targetHoursCalculation.compositeUnitRate && targetHoursCalculation.compositeUnitRate.unitRate != 0
                        ? bankersRound(
                            (targetHoursCalculation.crewHours +
                              targetHoursCalculation.indirectHours +
                              targetHoursCalculation.projectAdjustmentHours) /
                              targetHoursCalculation.compositeUnitRate.unitRate,
                            0
                          )
                        : 0
                    }`}
                    &nbsp;<Trans>LF</Trans>
                  </Text>
                </Box>
              </Box>
            )}
          </Box>
        </TargetCalculationStyled>
      </ErrorBoundary>
    </Box>
  );
};
